import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import { useTenant } from '../../../../providers/TenantProvider';
import { AdminBadge } from '../components/AdminBadge';
import { ALL_MODULES } from '../../../../config/modules';
import {
  fetchAdminUsers,
  adminRequest,
  fetchPersonas,
  fetchUserModuloAcceso,
  setUserModuloAcceso,
  type NivelModuloAcceso,
  type ModuloAccesoRow,
} from '../../../shared/services/api';

// 0615b: helper que devuelve nombre + descripcion de un modulo a partir
// del codigo MX. Si el codigo no esta en ALL_MODULES (modulo extra que no
// esta en la BD del tenant), devuelve el codigo como name + descripcion vacia.
function getModuloMeta(code: string): { name: string; description: string } {
  const m = ALL_MODULES.find((x) => x.code === code);
  return {
    name: m?.name ?? code,
    description: m?.description ?? '',
  };
}

// AdminUsuarios — Tab Usuarios (PRD-002 0572)
// CRUD users del tenant. TENANT_AUDITOR ve todo en read-only.
// Invitar + Importar CSV operativos contra POST /admin/users/invite.

interface UserRow {
  id: string; // 0577: necesario para PUT/DELETE
  nombre: string;
  email: string;
  rol: string;
  activo: boolean;
  ultimo: string;
  // 0593 Gap B: persona del organigrama vinculada (M1 Structure)
  personaId: string | null;
  personaCodigo: string | null;
  personaNombre: string | null;
  // 0615a ADR-024: accesos especiales por modulo (solo filas != VIEWER).
  // Usado para pintar badges en columna "Accesos especiales" sin N+1 queries.
  modulosAcceso: ModuloAccesoRow[];
  // 0615e/f: TODOS los puestos activos del empleado vinculado (M1 Structure).
  // Una persona puede tener varios puestos a la vez (multi-puesto). Array vacio
  // si la persona no tiene asignaciones activas o no hay persona vinculada.
  // Ordenados: esPrincipal=true primero, luego mas reciente por fechaInicio.
  puestos: Array<{ codigo: string | null; titulo: string; esPrincipal: boolean }>;
}

const EDITABLE_ROLES = [
  'SUPERADMIN_PLATFORM',
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'TENANT_AUDITOR',
  'MEMBER',
] as const;

const ROLE_FILTER = [
  'Todos',
  'SUPERADMIN_PLATFORM',
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'TENANT_AUDITOR',
  'MEMBER',
];

const C = {
  OSCURO: '#0E243E',
  AZUL1: '#1F3864',
  AZUL2: '#2F5496',
  CELESTE: '#33BCE1',
  MEDIO: '#16507D',
  CLARO: '#1A94BF',
  SUAVE: '#D6E4F0',
  ACENTO_SUAVE: '#E8EFF9',
  GRIS: '#F2F2F2',
  GRIS_MEDIO: '#D9D9D9',
  GRIS_OSCURO: '#595959',
  VERDE_BG: '#F0FDF4',
  VERDE_TXT: '#15803D',
  ROJO_BG: '#FEF2F2',
  ROJO_TXT: '#DC2626',
};

const TH: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#fff',
};

const INPUT: React.CSSProperties = {
  padding: '6px 10px',
  border: `1px solid ${C.GRIS_MEDIO}`,
  borderRadius: 6,
  fontSize: 11,
  fontFamily: 'inherit',
  outline: 'none',
};

const ROLES_DATA = [
  { rol: 'SUPERADMIN_PLATFORM', color: C.OSCURO, desc: 'Acceso total cross-tenant (VALOR·IA)' },
  { rol: 'TENANT_OWNER', color: C.AZUL2, desc: 'Dueño del tenant — acceso total' },
  { rol: 'TENANT_ADMIN', color: C.MEDIO, desc: 'Admin operativo del tenant' },
  { rol: 'TENANT_AUDITOR', color: C.CLARO, desc: 'Solo lectura + auditoría' },
  { rol: 'MEMBER', color: C.GRIS_OSCURO, desc: 'Usuario operativo (sin acceso a Configuración)' },
];

const RBAC_DATA = [
  { e: 'Tenant config', SUPER: 'CRUD', OWN: 'CRUD', ADM: 'RU', AUD: 'R', MBR: '—' },
  { e: 'Usuarios', SUPER: 'CRUD', OWN: 'CRUD', ADM: 'CRU', AUD: 'R', MBR: '—' },
  { e: 'Procesos', SUPER: 'CRUD', OWN: 'CRUD', ADM: 'CRUD', AUD: 'R', MBR: 'R' },
  { e: 'Documentos', SUPER: 'CRUDAP', OWN: 'CRUDAP', ADM: 'CRUP', AUD: 'R', MBR: 'RU' },
  { e: 'Organigramas', SUPER: 'CRUD', OWN: 'CRUD', ADM: 'CRUD', AUD: 'R', MBR: 'R' },
  { e: 'Auditoría', SUPER: 'CRUD', OWN: 'R', ADM: 'R', AUD: 'R', MBR: '—' },
];

function permColor(v: string): string {
  if (v === '—') return C.GRIS_MEDIO;
  if (v.length > 3) return C.VERDE_TXT;
  return C.AZUL2;
}

// 0596: MenuItem y MenuDivider eliminados (eran del dropdown 3-dots, hoy
// reemplazado por modal unificado openManage).

// 0588 fix #6: TH clickable con flecha sort
function SortableTh<K extends string>({
  label,
  sortKey,
  current,
  order,
  onSort,
}: {
  label: string;
  sortKey: K;
  current: K;
  order: 'asc' | 'desc';
  onSort: (k: K) => void;
}) {
  const active = current === sortKey;
  return (
    <th
      style={{ ...TH, textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(sortKey)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>
          {active ? (order === 'asc' ? '▲' : '▼') : '▲'}
        </span>
      </span>
    </th>
  );
}

export function AdminUsuarios() {
  const { t } = useI18n();
  const tu = t.admin.usuarios;
  const { user, isImpersonating } = useAuth();
  const role = user?.role ?? 'MEMBER';
  // 0572: solo TENANT_OWNER/TENANT_ADMIN/SUPERADMIN_PLATFORM pueden mutar
  const canEdit =
    role === 'SUPERADMIN_PLATFORM' || role === 'TENANT_OWNER' || role === 'TENANT_ADMIN';
  // 0588 fix #3: SUPERADMIN sin impersonate ve solo su tenant interno - banner explica
  const isSuperadminOwn = role === 'SUPERADMIN_PLATFORM' && !isImpersonating;

  // 0598 Gap H: filtros persistidos en URL (compartibles + sobrevive recarga)
  const [searchParams, setSearchParams] = useSearchParams();
  // 0602 Opcion A paso 2: navegar al detalle de la persona en Estructura
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserRow[]>([]);
  const [totalUsers, setTotalUsers] = useState(0); // 0588 fix #5: paginacion real
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [rolFilter, setRolFilter] = useState(searchParams.get('rol') ?? 'Todos');
  const [showRbac, setShowRbac] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNombre, setInviteNombre] = useState(''); // 0576: input opcional, deriva de email si vacio
  const [inviteRol, setInviteRol] = useState<string>('MEMBER');
  const [inviting, setInviting] = useState(false);
  // 0603 Opcion A paso 3: modal Invitar permite vincular persona existente
  // (recomendado) o crear persona nueva. La logica de "crear" la maneja el
  // backend en 0604 dentro de una transaccion.
  const [inviteMode, setInviteMode] = useState<'persona' | 'nueva'>('persona');
  const [inviteSelectedPersonaId, setInviteSelectedPersonaId] = useState<string>('');
  const [inviteApellidos, setInviteApellidos] = useState('');
  const [inviteTipoPersona, setInviteTipoPersona] = useState<
    'EMPLEADO' | 'COLABORADOR' | 'EXTERNO'
  >('EMPLEADO');
  const [availablePersonas, setAvailablePersonas] = useState<
    Array<{
      id: string;
      codigo: string;
      nombre: string;
      apellidos: string;
      email: string;
      tipoPersona: 'EMPLEADO' | 'COLABORADOR' | 'EXTERNO';
    }>
  >([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [importing, setImporting] = useState(false);
  // 0580: M17 Notify stub - si no hay Resend, mostramos password al admin
  const [invitePasswordTemp, setInvitePasswordTemp] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'ok' | 'warn' } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 0588 fix #5: paginacion real (10/25/50/100 por pagina) + 0598: persistido URL
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1') || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize') ?? '25') || 25);

  // 0588 fix #6: sort por columna + 0598: persistido URL
  type SortKey = 'nombre' | 'email' | 'rol' | 'activo' | 'ultimo';
  const SORT_KEYS: readonly SortKey[] = ['nombre', 'email', 'rol', 'activo', 'ultimo'];
  const initSortBy = (searchParams.get('sortBy') as SortKey | null) ?? 'nombre';
  const initSortOrder = searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc';
  const [sortBy, setSortBy] = useState<SortKey>(
    SORT_KEYS.includes(initSortBy) ? initSortBy : 'nombre',
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initSortOrder);
  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };

  // 0588 fix #4: ConfirmModal custom (reemplaza confirm() nativo)
  const [confirmModal, setConfirmModal] = useState<{
    titulo: string;
    mensaje: string;
    danger?: boolean;
    onConfirm: () => void | Promise<void>;
  } | null>(null);

  // 0596: Modal unificado de gestion de usuario (reemplaza dropdown 3-dots).
  // Click en fila -> abre modal con todas las acciones (datos, permisos, acciones).
  const [manageUser, setManageUser] = useState<UserRow | null>(null);
  const [manageNombre, setManageNombre] = useState('');
  const [manageRol, setManageRol] = useState('MEMBER');
  const [manageActivo, setManageActivo] = useState(true);
  const [managePersonaId, setManagePersonaId] = useState<string>('');
  const [savingManage, setSavingManage] = useState(false);
  // 0599 Gap I: estado lockout del usuario en modal manage
  const [manageLockout, setManageLockout] = useState<{
    locked: boolean;
    attempts: number;
    retryAfterSec: number;
  } | null>(null);
  // 0613 ADR-024: matriz acceso por modulo (solo aplicable a MEMBERs).
  // Map modulo -> nivel; modulos sin entrada = VIEWER por default.
  const { tenant } = useTenant();
  const modulosActivos: string[] = tenant?.modulosActivos ?? [];
  const [manageModuloAccesos, setManageModuloAccesos] = useState<Record<string, NivelModuloAcceso>>(
    {},
  );
  const [savingModuloAccesos, setSavingModuloAccesos] = useState(false);
  const openManage = async (u: UserRow) => {
    setManageUser(u);
    setManageNombre(u.nombre);
    setManageRol(u.rol || 'MEMBER');
    setManageActivo(u.activo);
    setManagePersonaId(u.personaId ?? '');
    setManageLockout(null);
    setManageModuloAccesos({});
    // 0599: carga lockout-status en paralelo (no bloquea apertura del modal)
    if (u.id) {
      adminRequest<{ data: { locked: boolean; attempts: number; retryAfterSec: number } }>(
        `/users/${u.id}/lockout-status`,
      )
        .then((res) => setManageLockout(res.data))
        .catch(() => setManageLockout(null));
    }
    // 0613: cargar accesos por modulo si el usuario es MEMBER (los demas
    // roles tienen niveles implicitos y no necesitan filas).
    if (u.id && u.rol === 'MEMBER') {
      fetchUserModuloAcceso(u.id)
        .then((res) => {
          const map: Record<string, NivelModuloAcceso> = {};
          for (const a of res.data?.accesos ?? []) map[a.modulo] = a.nivel;
          setManageModuloAccesos(map);
        })
        .catch(() => setManageModuloAccesos({}));
    }
    // carga lazy de personas para el select
    if (personasList.length === 0) {
      try {
        const res = await fetchPersonas({ page: '1', porPagina: '500' });
        if (Array.isArray(res.data)) {
          setPersonasList(
            res.data.map((p) => ({
              id: p.id,
              nombre: p.nombre ?? '',
              apellidos: p.apellidos ?? null,
              codigo: p.codigo ?? null,
            })),
          );
        }
      } catch {
        // noop - select queda vacio
      }
    }
  };
  const handleUnlock = async () => {
    if (!canEdit || !manageUser?.id) return;
    try {
      await adminRequest(`/users/${manageUser.id}/unlock`, { method: 'POST' });
      showToast(`Cuenta desbloqueada: ${manageUser.email}`);
      setManageLockout({ locked: false, attempts: 0, retryAfterSec: 0 });
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al desbloquear', 'warn');
    }
  };
  const handleSaveManage = async () => {
    if (!canEdit || !manageUser?.id) return;
    const isMeManage = !!user?.userId && manageUser.id === user.userId;
    setSavingManage(true);
    try {
      const payload: Record<string, unknown> = {};
      // 0602 Opcion A paso 2: si hay persona vinculada NO enviar nombreCompleto
      // (Persona = fuente de verdad). Defensa en profundidad ante manipulacion
      // del DOM disabled.
      if (!manageUser.personaId && manageNombre !== manageUser.nombre) {
        payload.nombreCompleto = manageNombre;
      }
      if (!isMeManage && manageRol !== manageUser.rol) payload.rol = manageRol;
      if (!isMeManage && manageActivo !== manageUser.activo) payload.activo = manageActivo;
      if ((managePersonaId || null) !== manageUser.personaId) {
        payload.personaId = managePersonaId || null;
      }
      if (Object.keys(payload).length === 0) {
        showToast('Sin cambios');
        setManageUser(null);
        return;
      }
      await adminRequest(`/users/${manageUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      showToast(`Cambios guardados para ${manageUser.email}`);
      setManageUser(null);
      loadUsers();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al guardar', 'warn');
    } finally {
      setSavingManage(false);
    }
  };

  // 0613 ADR-024: guardar matriz acceso por modulo del MEMBER en gestion.
  const handleSaveAccesos = async () => {
    if (!manageUser?.id) return;
    setSavingModuloAccesos(true);
    try {
      const accesos = modulosActivos.map((modulo) => ({
        modulo,
        nivel: manageModuloAccesos[modulo] ?? 'VIEWER',
      }));
      await setUserModuloAcceso(manageUser.id, accesos);
      showToast(`Acceso por modulo actualizado para ${manageUser.email}`);
      // 0615e fix: refrescar tabla principal para que la columna "Accesos
      // especiales" muestre los nuevos badges sin requerir cierre+reapertura.
      loadUsers();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al guardar accesos', 'warn');
    } finally {
      setSavingModuloAccesos(false);
    }
  };

  // 0593 Gap B: asignar persona del organigrama
  const [assignPersonaUser, setAssignPersonaUser] = useState<UserRow | null>(null);
  const [personasList, setPersonasList] = useState<
    Array<{
      id: string;
      nombre: string;
      apellidos: string | null;
      codigo: string | null;
    }>
  >([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>('');
  const [savingPersona, setSavingPersona] = useState(false);

  const showToast = (msg: string, type: 'ok' | 'warn' = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // 0588 fix #5: paginacion real + 0597 Gap G: sort cross-page server-side
  const loadUsers = () => {
    fetchAdminUsers(page, pageSize, {
      sortBy,
      sortOrder,
      rol: rolFilter !== 'Todos' ? rolFilter : undefined,
      q: search || undefined,
    })
      .then((res) => {
        if (Array.isArray(res.data)) {
          setUsers(
            res.data.map((u) => {
              // 0593 Gap B: persona del organigrama (M1 Structure)
              // 0615e/f: persona trae TODAS las asignaciones activas (multi-puesto).
              const persona = (
                u as {
                  persona?: {
                    id: string;
                    nombre: string;
                    apellidos: string | null;
                    codigo: string | null;
                    asignaciones?: Array<{
                      puesto: { id: string; codigo: string | null; titulo: string };
                      esPrincipal: boolean;
                    }>;
                  };
                }
              ).persona;
              // 0601 Opcion A paso 1: si hay persona vinculada usarla como
              // fuente de verdad del nombre. Usuario.nombreCompleto queda como
              // fallback hasta migracion completa (0605/0606).
              const personaNombre = persona
                ? `${persona.nombre}${persona.apellidos ? ' ' + persona.apellidos : ''}`.trim()
                : null;
              // 0615e/f: lista completa de puestos activos (ya ordenada en BE).
              const puestos = (persona?.asignaciones ?? []).map((a) => ({
                codigo: a.puesto.codigo ?? null,
                titulo: a.puesto.titulo,
                esPrincipal: a.esPrincipal,
              }));
              // 0615a ADR-024: backend listUsers ahora devuelve modulosAcceso[]
              // (solo filas != VIEWER). Las pintamos en columna "Accesos especiales".
              const modulosAcceso =
                (
                  u as {
                    modulosAcceso?: Array<{ modulo: string; nivel: NivelModuloAcceso }>;
                  }
                ).modulosAcceso ?? [];
              return {
                id: u.id || '', // 0577: necesario para acciones
                nombre: personaNombre || u.nombreCompleto || u.nombre || '',
                email: u.email || '',
                rol: u.rol || '',
                activo: Boolean(u.activo),
                // 0588 fix #1: backend ya devuelve ultimoAcceso (antes daba undefined)
                ultimo: u.ultimoAcceso
                  ? new Date(u.ultimoAcceso).toLocaleString('es-ES', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—',
                personaId: persona?.id ?? null,
                personaCodigo: persona?.codigo ?? null,
                personaNombre,
                modulosAcceso,
                puestos,
              };
            }),
          );
          // 0588 fix #5: total para calcular paginas
          const meta = (res as { meta?: { total?: number } }).meta;
          if (meta?.total !== undefined) setTotalUsers(meta.total);
        }
      })
      .catch((err: Error) => {
        showToast(`Error cargando usuarios: ${err.message}`, 'warn');
      });
  };

  // 0577: menu contextual del 3-dots por fila
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editRoleUser, setEditRoleUser] = useState<UserRow | null>(null);
  const [editingRole, setEditingRole] = useState<string>('MEMBER');
  const [savingRole, setSavingRole] = useState(false);

  // Cierra menu al click fuera
  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  // 0610: deep-link desde AdminPersonaPanel (Estructura -> Administracion -> persona).
  // Soporta dos modos:
  //   ?personaId=X        -> busca usuario vinculado a X y abre modal Gestionar
  //   ?invitarPersonaId=X -> abre modal Invitar pre-seleccionando X en modo persona
  // Limpia el query param tras consumirlo (replace history).
  useEffect(() => {
    const personaIdLink = searchParams.get('personaId');
    const invitarPersonaIdLink = searchParams.get('invitarPersonaId');
    if (!personaIdLink && !invitarPersonaIdLink) return;

    const next = new URLSearchParams(searchParams);
    next.delete('personaId');
    next.delete('invitarPersonaId');
    setSearchParams(next, { replace: true });

    if (personaIdLink) {
      // Buscar usuario con esta persona vinculada
      adminRequest<{
        data: Array<{
          id: string;
          email: string;
          nombreCompleto: string;
          rol: string;
          activo: boolean;
          ultimoAcceso?: string | null;
          persona?: {
            id: string;
            nombre: string;
            apellidos: string | null;
            codigo: string | null;
          } | null;
        }>;
      }>(`/users?personaId=${personaIdLink}&limit=1`)
        .then((res) => {
          const u = res.data?.[0];
          if (!u) {
            showToast('Esta persona no tiene usuario vinculado', 'warn');
            return;
          }
          const personaNombreCalc = u.persona
            ? `${u.persona.nombre}${u.persona.apellidos ? ' ' + u.persona.apellidos : ''}`.trim()
            : null;
          openManage({
            id: u.id,
            nombre: personaNombreCalc || u.nombreCompleto || '',
            email: u.email || '',
            rol: u.rol || '',
            activo: Boolean(u.activo),
            ultimo: u.ultimoAcceso
              ? new Date(u.ultimoAcceso).toLocaleString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—',
            personaId: u.persona?.id ?? null,
            personaCodigo: u.persona?.codigo ?? null,
            personaNombre: personaNombreCalc,
            // 0615a: deep-link no necesita modulosAcceso para abrir el modal -
            // openManage() volvera a hacer fetch GET /users/:id/modulo-acceso.
            modulosAcceso: [],
            // 0615f: deep-link abre modal directo, no necesita lista de puestos.
            puestos: [],
          });
        })
        .catch((err: Error) => {
          showToast(`Error cargando usuario: ${err.message}`, 'warn');
        });
    } else if (invitarPersonaIdLink) {
      // Abrir modal Invitar pre-seleccionando esta persona en modo "vincular existente"
      setInviteMode('persona');
      setInviteSelectedPersonaId(invitarPersonaIdLink);
      setShowInviteModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 0596: handleToggleActivo y openEditRole eliminados - acciones movidas al
  // modal manage. handleDelete y handleResetPassword siguen vigentes (acciones
  // destructivas que el modal delega).
  const handleDelete = (u: UserRow) => {
    if (!canEdit || !u.id) return;
    setOpenMenuId(null);
    setConfirmModal({
      titulo: 'Eliminar usuario',
      mensaje: `¿Eliminar definitivamente a ${u.nombre} (${u.email})? Esta accion no se puede deshacer.`,
      danger: true,
      onConfirm: async () => {
        try {
          await adminRequest(`/users/${u.id}`, { method: 'DELETE' });
          showToast(`Usuario ${u.nombre} eliminado`);
          loadUsers();
        } catch (err) {
          showToast(err instanceof Error ? err.message : 'Error al eliminar usuario', 'warn');
        }
      },
    });
  };

  // 0595 Gap F: bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActing, setBulkActing] = useState(false);
  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (visible: UserRow[]) => {
    setSelectedIds((prev) => {
      const allSelected = visible.every((u) => prev.has(u.id));
      const next = new Set(prev);
      if (allSelected) {
        for (const u of visible) next.delete(u.id);
      } else {
        for (const u of visible) next.add(u.id);
      }
      return next;
    });
  };
  const handleBulkAction = (action: 'activate' | 'deactivate' | 'delete') => {
    if (selectedIds.size === 0) return;
    const labels = {
      activate: { titulo: 'Activar usuarios', verbo: 'activar' },
      deactivate: { titulo: 'Desactivar usuarios', verbo: 'desactivar' },
      delete: { titulo: 'Eliminar usuarios', verbo: 'eliminar' },
    } as const;
    const cfg = labels[action];
    setConfirmModal({
      titulo: cfg.titulo,
      mensaje: `Vas a ${cfg.verbo} ${selectedIds.size} usuario(s). Continuar?`,
      danger: action !== 'activate',
      onConfirm: async () => {
        setBulkActing(true);
        try {
          const ids = Array.from(selectedIds);
          const res = await adminRequest<{
            data: { ok: number; fail: number; results: Array<{ ok: boolean; error?: string }> };
          }>('/users/bulk', {
            method: 'POST',
            body: JSON.stringify({ userIds: ids, action }),
          });
          const d = res.data;
          if (d.fail > 0) {
            showToast(`Bulk: ${d.ok} ok, ${d.fail} fallaron (revisa permisos OWNER/self)`, 'warn');
          } else {
            showToast(`Bulk ${cfg.verbo}: ${d.ok} usuario(s) procesados`);
          }
          setSelectedIds(new Set());
          loadUsers();
        } catch (err) {
          showToast(err instanceof Error ? err.message : 'Error en bulk action', 'warn');
        } finally {
          setBulkActing(false);
        }
      },
    });
  };

  // 0594 Gap E: exportar usuarios a CSV (mismo patron que el del audit log)
  const [exportingUsersCsv, setExportingUsersCsv] = useState(false);
  const handleExportUsersCsv = async () => {
    setExportingUsersCsv(true);
    try {
      const token = localStorage.getItem('vianet-token');
      const res = await fetch('/api/v1/admin/users/export.csv', {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usuarios-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('CSV de usuarios descargado');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al exportar CSV', 'warn');
    } finally {
      setExportingUsersCsv(false);
    }
  };

  // 0596: openAssignPersona y handleSavePersona DEPRECATED - reemplazados por
  // el modal manage. Se mantienen aqui solo porque el JSX del modal viejo
  // (assignPersonaUser) sigue presente como fallback - eliminar en limpieza
  // posterior junto con state assignPersonaUser/selectedPersonaId/savingPersona.
  const handleSavePersona = async () => {
    if (!canEdit || !assignPersonaUser?.id) return;
    setSavingPersona(true);
    try {
      await adminRequest(`/users/${assignPersonaUser.id}`, {
        method: 'PUT',
        body: JSON.stringify({ personaId: selectedPersonaId || null }),
      });
      showToast(
        selectedPersonaId
          ? `Persona asignada a ${assignPersonaUser.email}`
          : `Persona desvinculada de ${assignPersonaUser.email}`,
      );
      setAssignPersonaUser(null);
      loadUsers();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al asignar persona', 'warn');
    } finally {
      setSavingPersona(false);
    }
  };

  // 0592 Gap C: reenviar invitacion / reset password forzado.
  // Mismo flow que invitar: si Resend ausente, se devuelve password al admin.
  const handleResetPassword = (u: UserRow) => {
    if (!canEdit || !u.id) return;
    setOpenMenuId(null);
    setConfirmModal({
      titulo: 'Resetear contrasena',
      mensaje: `Se generara una nueva contrasena temporal para ${u.nombre} (${u.email}). Si el envio por email no esta configurado, se mostrara aqui para que la copies. Continuar?`,
      onConfirm: async () => {
        try {
          const res = await adminRequest<{
            data: {
              usuario: { email: string };
              passwordTemporal?: string;
              emailEnviado: boolean;
            };
          }>(`/users/${u.id}/reset-password`, { method: 'POST' });
          const d = res.data;
          if (d.emailEnviado) {
            showToast(`Nueva contrasena enviada por email a ${u.email}`);
          } else if (d.passwordTemporal) {
            setInvitePasswordTemp({ email: u.email, password: d.passwordTemporal });
          } else {
            showToast(`Contrasena reseteada para ${u.email}`);
          }
        } catch (err) {
          showToast(err instanceof Error ? err.message : 'Error al resetear', 'warn');
        }
      },
    });
  };

  const handleSaveRole = async () => {
    if (!canEdit || !editRoleUser?.id) return;
    setSavingRole(true);
    try {
      await adminRequest(`/users/${editRoleUser.id}`, {
        method: 'PUT',
        body: JSON.stringify({ rol: editingRole }),
      });
      showToast(`Rol actualizado: ${editRoleUser.email} -> ${editingRole}`);
      setEditRoleUser(null);
      loadUsers();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al cambiar rol', 'warn');
    } finally {
      setSavingRole(false);
    }
  };

  // 0588 fix #5: re-cargar al cambiar pagina o pageSize
  // 0597 Gap G: re-cargar al cambiar sortBy/sortOrder/rolFilter/search (debounce 300ms para search)
  useEffect(() => {
    const t = setTimeout(loadUsers, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder, rolFilter, search]);

  // 0598 Gap H: sincronizar state -> URL search params (compartibles + back/forward)
  // Solo escribimos params cuando difieren del default para mantener URL limpia.
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    if (pageSize !== 25) params.set('pageSize', String(pageSize));
    if (sortBy !== 'nombre') params.set('sortBy', sortBy);
    if (sortOrder !== 'asc') params.set('sortOrder', sortOrder);
    if (rolFilter !== 'Todos') params.set('rol', rolFilter);
    if (search) params.set('q', search);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder, rolFilter, search]);

  // 0603 Opcion A paso 3: al abrir modal Invitar cargar personas sin Usuario
  // vinculado para el modo "vincular existente". Filtro client-side: cargamos
  // personas + usuarios y restamos los IDs ya usados. Para tenants con miles
  // de personas se podria mover a server-side anadiendo ?sinUsuario=true al
  // endpoint /apps/personas en el futuro.
  useEffect(() => {
    if (!showInviteModal) return;
    setLoadingPersonas(true);
    Promise.all([
      fetchPersonas({ limit: '500' }),
      adminRequest<{
        data: Array<{ personaId: string | null }>;
      }>('/users?limit=500'),
    ])
      .then(([pRes, uRes]) => {
        const usedIds = new Set(
          (uRes.data || []).map((u) => u.personaId).filter((id): id is string => Boolean(id)),
        );
        const personas = (pRes.data || [])
          .filter((p) => !usedIds.has(p.id))
          .map((p) => ({
            id: p.id,
            codigo: p.codigo,
            nombre: p.nombre,
            apellidos: p.apellidos ?? '',
            email: p.email ?? '',
            tipoPersona: p.tipoPersona,
          }));
        setAvailablePersonas(personas);
        // Si no hay personas disponibles, forzar modo "nueva"
        if (personas.length === 0) setInviteMode('nueva');
        // 0610: si hay persona pre-seleccionada por deep-link, autocompletar
        // email y nombre desde sus datos para no obligar al admin a re-elegir.
        if (inviteSelectedPersonaId) {
          const p = personas.find((x) => x.id === inviteSelectedPersonaId);
          if (p) {
            if (!inviteEmail) setInviteEmail(p.email);
            if (!inviteNombre) setInviteNombre(`${p.nombre} ${p.apellidos}`.trim());
          } else {
            // Persona pre-seleccionada NO esta disponible (ya tiene usuario).
            // Aviso al admin y limpio la seleccion.
            showToast(
              'Esta persona ya tiene un usuario digital vinculado. Selecciona otra.',
              'warn',
            );
            setInviteSelectedPersonaId('');
          }
        }
      })
      .catch((err: Error) => {
        showToast(`Error cargando personas: ${err.message}`, 'warn');
        setInviteMode('nueva');
      })
      .finally(() => setLoadingPersonas(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInviteModal]);

  // 0576: deriva nombre legible del email si el input nombre esta vacio
  // ej: "alex.garcia@valoria.es" -> "Alex Garcia"
  const deriveNombreFromEmail = (email: string): string => {
    const local = email.split('@')[0] ?? '';
    if (!local) return '';
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(' ');
  };

  // 0572 + fix 0576: invitar usuario via POST /admin/users/invite
  // Backend exige nombre obligatorio (Zod). Si el input nombre esta vacio,
  // derivamos del email automaticamente.
  // 0603 Opcion A paso 3: payload extendido segun modo:
  //   - persona: { email, nombre, rol, personaId }
  //   - nueva:   { email, nombre, rol, persona: { nombre, apellidos, email, tipoPersona } }
  // El backend procesara los campos extra en 0604 (hoy los strippea Zod).
  const handleInvite = async () => {
    if (!canEdit) return;
    let email = inviteEmail.trim();
    let nombre = inviteNombre.trim();
    let apellidos = inviteApellidos.trim();
    let personaIdSeleccionada: string | null = null;

    if (inviteMode === 'persona') {
      if (!inviteSelectedPersonaId) {
        showToast('Selecciona una persona del directorio', 'warn');
        return;
      }
      const p = availablePersonas.find((x) => x.id === inviteSelectedPersonaId);
      if (!p) {
        showToast('Persona no valida', 'warn');
        return;
      }
      // Si admin no editó, usamos los datos de la persona como fuente
      if (!email) email = p.email;
      if (!nombre) nombre = p.nombre;
      if (!apellidos) apellidos = p.apellidos;
      personaIdSeleccionada = p.id;
    }

    if (!email || !email.includes('@')) {
      showToast('Email invalido', 'warn');
      return;
    }
    const nombreFinal = nombre || deriveNombreFromEmail(email);
    if (!nombreFinal) {
      showToast('Nombre invalido', 'warn');
      return;
    }
    setInviting(true);
    try {
      const payload: Record<string, unknown> = {
        email,
        nombre:
          inviteMode === 'nueva' && apellidos ? `${nombreFinal} ${apellidos}`.trim() : nombreFinal,
        rol: inviteRol,
      };
      if (inviteMode === 'persona' && personaIdSeleccionada) {
        payload.personaId = personaIdSeleccionada;
      } else if (inviteMode === 'nueva') {
        payload.persona = {
          nombre: nombreFinal,
          apellidos,
          email,
          tipoPersona: inviteTipoPersona,
        };
      }
      const res = await adminRequest<{
        data: {
          usuario: { email: string };
          passwordTemporal?: string;
          emailEnviado: boolean;
        };
      }>('/users/invite', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // 0580: si M17 Notify stub devuelve emailEnviado=false, mostramos password
      // temporal al admin para que la copie. Cuando RESEND_API_KEY este configurada
      // y el envio sea OK, password viene vacia y solo se muestra confirmacion.
      const data = res.data;
      if (data.emailEnviado) {
        showToast(`Invitacion enviada a ${email} por email`);
      } else if (data.passwordTemporal) {
        // Mostrar password en modal de copia (no toast — son chars sensibles)
        setInvitePasswordTemp({ email, password: data.passwordTemporal });
      } else {
        showToast(`Usuario ${email} creado`);
      }
      setInviteEmail('');
      setInviteNombre('');
      setInviteApellidos('');
      setInviteSelectedPersonaId('');
      setInviteMode('persona');
      setInviteTipoPersona('EMPLEADO');
      setShowInviteModal(false);
      loadUsers();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al invitar', 'warn');
    } finally {
      setInviting(false);
    }
  };

  // 0572 + fix 0576: importar CSV (formato: email[,nombre[,rol]] — una linea por usuario)
  // Si nombre vacio: derivar del email. Si rol vacio: MEMBER por defecto.
  const handleImportCsv = async (file: File) => {
    if (!canEdit) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.startsWith('#'));
      let ok = 0;
      let fail = 0;
      for (const line of lines) {
        const parts = line.split(',').map((s) => s.trim());
        const email = parts[0];
        const nombreCsv = parts[1] || '';
        const rol = parts[2] || parts[1] || 'MEMBER';
        // Si parts[1] es un email-like o no existe asumimos formato email,rol (legacy 2-cols)
        const rolFinal = parts.length === 2 && /^[A-Z_]+$/.test(parts[1] ?? '') ? parts[1] : rol;
        const nombreFinal =
          nombreCsv && !/^[A-Z_]+$/.test(nombreCsv) ? nombreCsv : deriveNombreFromEmail(email);
        if (!email || !email.includes('@') || !nombreFinal) {
          fail++;
          continue;
        }
        try {
          await adminRequest('/users/invite', {
            method: 'POST',
            body: JSON.stringify({ email, nombre: nombreFinal, rol: rolFinal }),
          });
          ok++;
        } catch {
          fail++;
        }
      }
      showToast(`Import CSV completado: ${ok} ok, ${fail} fallos`, fail > 0 ? 'warn' : 'ok');
      loadUsers();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error leyendo CSV', 'warn');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // 0597 Gap G: filtros y sort movidos a server-side. El array `users` ya viene
  // filtrado/ordenado desde el backend. `filtered` solo expone alias para
  // compatibilidad con la UI existente (checkbox cabecera, indicador, paginacion).
  const filtered = users;

  // 0588 fix #5: helpers paginacion
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));
  const goToPage = (p: number) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== page) setPage(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── Barra de filtros ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...INPUT, width: 200 }}
          />
          <select
            value={rolFilter}
            onChange={(e) => setRolFilter(e.target.value)}
            style={{ ...INPUT, padding: '6px 8px' }}
          >
            {ROLE_FILTER.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImportCsv(file);
            }}
          />
          {/* 0594 Gap E: exportar CSV - visible para TENANT_AUDITOR tambien */}
          <button
            onClick={handleExportUsersCsv}
            disabled={exportingUsersCsv}
            style={{
              padding: '6px 12px',
              background: '#fff',
              border: `1px solid ${C.GRIS_OSCURO}`,
              color: C.GRIS_OSCURO,
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 600,
              cursor: exportingUsersCsv ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: exportingUsersCsv ? 0.5 : 1,
            }}
            title="Descargar CSV con todos los usuarios del tenant"
          >
            {exportingUsersCsv ? 'Exportando...' : 'Exportar CSV'}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={!canEdit || importing}
            style={{
              padding: '6px 12px',
              background: '#fff',
              border: `1px solid ${C.AZUL2}`,
              color: canEdit ? C.AZUL2 : '#9CA3AF',
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 600,
              cursor: !canEdit || importing ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: !canEdit || importing ? 0.5 : 1,
            }}
            title="CSV con formato: email,rol (una linea por usuario)"
          >
            {importing ? 'Importando...' : 'Importar CSV'}
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            disabled={!canEdit}
            style={{
              padding: '6px 12px',
              background: canEdit ? C.OSCURO : '#9CA3AF',
              border: 'none',
              color: '#fff',
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 600,
              cursor: canEdit ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              opacity: canEdit ? 1 : 0.5,
            }}
          >
            + Invitar
          </button>
        </div>
      </div>

      {/* 0588 fix #3: SUPERADMIN_PLATFORM sin impersonate solo ve su tenant interno */}
      {isSuperadminOwn && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 6,
            fontSize: 12,
            backgroundColor: '#FEF3C7',
            color: '#92400E',
            border: '1px solid #FDE68A',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <span>
            {tu.superadminTenantBanner.split('{link}')[0]}
            <a
              href="/admin/backoffice"
              style={{ color: '#92400E', fontWeight: 700, textDecoration: 'underline' }}
            >
              {tu.superadminTenantBannerLink}
            </a>
            {tu.superadminTenantBanner.split('{link}')[1]}
          </span>
        </div>
      )}

      {/* 0572: banner read-only TENANT_AUDITOR */}
      {!canEdit && (
        <div
          style={{
            padding: '8px 14px',
            borderRadius: 6,
            fontSize: 12,
            backgroundColor: '#EFF6FF',
            color: '#1E40AF',
            border: '1px solid #BFDBFE',
          }}
        >
          {tu.readOnlyBanner}
        </div>
      )}

      {/* 0595 Gap F: barra de bulk actions (visible cuando hay >=1 seleccionado) */}
      {canEdit && selectedIds.size > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 14px',
            borderRadius: 6,
            background: C.OSCURO,
            color: '#fff',
            fontSize: 12,
            gap: 12,
          }}
        >
          <div>
            <strong>{selectedIds.size}</strong> usuario(s) seleccionado(s)
            <button
              onClick={() => setSelectedIds(new Set())}
              style={{
                marginLeft: 8,
                background: 'transparent',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                fontFamily: 'inherit',
                textDecoration: 'underline',
                opacity: 0.85,
              }}
            >
              Limpiar
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => handleBulkAction('activate')}
              disabled={bulkActing}
              style={{
                padding: '6px 12px',
                background: '#fff',
                color: C.OSCURO,
                border: 'none',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                cursor: bulkActing ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                opacity: bulkActing ? 0.5 : 1,
              }}
            >
              Activar
            </button>
            <button
              onClick={() => handleBulkAction('deactivate')}
              disabled={bulkActing}
              style={{
                padding: '6px 12px',
                background: '#fff',
                color: C.OSCURO,
                border: 'none',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                cursor: bulkActing ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                opacity: bulkActing ? 0.5 : 1,
              }}
            >
              Desactivar
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              disabled={bulkActing}
              style={{
                padding: '6px 12px',
                background: C.ROJO_TXT,
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                cursor: bulkActing ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                opacity: bulkActing ? 0.5 : 1,
              }}
            >
              Eliminar
            </button>
          </div>
        </div>
      )}

      {/* ── Tabla de usuarios ── */}
      <div style={{ borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: C.OSCURO }}>
              {/* 0595 Gap F: checkbox cabecera para seleccionar todos los visibles */}
              {canEdit && (
                <th style={{ ...TH, width: 32, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todos"
                    checked={filtered.length > 0 && filtered.every((u) => selectedIds.has(u.id))}
                    onChange={() => toggleSelectAll(filtered)}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
              )}
              {/* 0588 fix #6: headers clickables con flecha sort */}
              <SortableTh
                label="Nombre"
                sortKey="nombre"
                current={sortBy}
                order={sortOrder}
                onSort={handleSort}
              />
              <SortableTh
                label="Email"
                sortKey="email"
                current={sortBy}
                order={sortOrder}
                onSort={handleSort}
              />
              {/* 0615e: nueva columna "Puesto" - puesto operativo activo
                  de la persona vinculada (M1 Structure). No sortable porque
                  el dato vive en una relacion (persona.asignaciones[0]). */}
              <th style={{ ...TH, textAlign: 'left' }}>Puesto</th>
              <SortableTh
                label="Rol"
                sortKey="rol"
                current={sortBy}
                order={sortOrder}
                onSort={handleSort}
              />
              <SortableTh
                label="Estado"
                sortKey="activo"
                current={sortBy}
                order={sortOrder}
                onSort={handleSort}
              />
              <SortableTh
                label="Último acceso"
                sortKey="ultimo"
                current={sortBy}
                order={sortOrder}
                onSort={handleSort}
              />
              {/* 0615a ADR-024: columna Accesos especiales (no sortable - es agregado).
                  Pinta badges para MEMBERs con EDITOR/ADMIN explicito por modulo.
                  Roles admin (SUPER/OWNER/TENANT_ADMIN) ven "(todos)"; AUDITOR ve "(solo lectura)". */}
              <th style={{ ...TH, textAlign: 'left' }}>Accesos especiales</th>
              <th style={{ ...TH, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((u, idx) => {
              // 0591 Gap D: detectar fila propia (yo mismo)
              const isMe = !!user?.userId && u.id === user.userId;
              return (
                <tr
                  key={idx}
                  onClick={() => openManage(u)}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    backgroundColor: isMe ? C.ACENTO_SUAVE : idx % 2 === 1 ? C.SUAVE : undefined,
                    cursor: 'pointer',
                  }}
                >
                  {/* 0595 Gap F: checkbox de seleccion (oculto en fila propia) */}
                  {canEdit && (
                    <td
                      style={{ padding: '10px 14px', textAlign: 'center', width: 32 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!isMe && (
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${u.nombre}`}
                          checked={selectedIds.has(u.id)}
                          onChange={() => toggleSelected(u.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </td>
                  )}
                  <td
                    style={{ padding: '10px 14px', fontWeight: 600, fontSize: 12, color: C.OSCURO }}
                  >
                    {u.nombre}
                    {isMe && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.AZUL2,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {tu.tuBadge}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: C.GRIS_OSCURO }}>
                    {u.email}
                  </td>
                  {/* 0615e/f: celda Puesto - lista de TODOS los puestos activos
                      del empleado (multi-puesto). El primero es el principal
                      (esPrincipal=true desde BE). Em-dash si no hay puestos. */}
                  <td
                    style={{
                      padding: '10px 14px',
                      fontSize: 12,
                      color: C.GRIS_OSCURO,
                      maxWidth: 240,
                    }}
                    title={
                      u.puestos.length > 0
                        ? u.puestos
                            .map(
                              (p) =>
                                `${p.codigo ? p.codigo + ' · ' : ''}${p.titulo}${p.esPrincipal ? ' (principal)' : ''}`,
                            )
                            .join('\n')
                        : undefined
                    }
                  >
                    {u.puestos.length === 0 ? (
                      <span style={{ color: C.GRIS_MEDIO }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {u.puestos.map((p, i) => (
                          <div
                            key={i}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              minWidth: 0,
                              borderLeft: p.esPrincipal
                                ? `2px solid ${C.AZUL2}`
                                : `2px solid transparent`,
                              paddingLeft: 6,
                            }}
                          >
                            <span
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              {p.titulo}
                            </span>
                            {p.esPrincipal && u.puestos.length > 1 && (
                              <span
                                style={{
                                  fontSize: 8,
                                  fontWeight: 700,
                                  color: C.AZUL2,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.4px',
                                  flexShrink: 0,
                                }}
                              >
                                PRINCIPAL
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <AdminBadge bg={C.ACENTO_SUAVE} color={C.AZUL2}>
                      {u.rol}
                    </AdminBadge>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {u.activo ? (
                      <AdminBadge bg={C.VERDE_BG} color={C.VERDE_TXT}>
                        Activo
                      </AdminBadge>
                    ) : (
                      <AdminBadge bg={C.ROJO_BG} color={C.ROJO_TXT}>
                        Inactivo
                      </AdminBadge>
                    )}
                  </td>
                  <td
                    style={{
                      padding: '10px 14px',
                      fontSize: 11,
                      fontFamily: 'monospace',
                      color: C.GRIS_OSCURO,
                    }}
                  >
                    {u.ultimo}
                  </td>
                  {/* 0615a ADR-024: celda Accesos especiales.
                      MEMBER -> N badges EDITOR/ADMIN por modulo (o "—" si todos VIEWER).
                      AUDITOR -> "(solo lectura)" gris.
                      SUPERADMIN/OWNER/TENANT_ADMIN -> "(todos)" en azul (admin implicito). */}
                  <td style={{ padding: '10px 14px' }}>
                    {u.rol === 'TENANT_AUDITOR' ? (
                      <span style={{ fontSize: 11, color: C.GRIS_OSCURO, fontStyle: 'italic' }}>
                        (solo lectura)
                      </span>
                    ) : u.rol === 'SUPERADMIN_PLATFORM' ||
                      u.rol === 'TENANT_OWNER' ||
                      u.rol === 'TENANT_ADMIN' ? (
                      <span style={{ fontSize: 11, color: C.AZUL2, fontWeight: 600 }}>(todos)</span>
                    ) : u.modulosAcceso.length === 0 ? (
                      <span style={{ fontSize: 12, color: C.GRIS_MEDIO }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.modulosAcceso.map((acc) => {
                          // 0615b: tooltip muestra nombre completo del modulo
                          // ademas del codigo + nivel para que el admin no
                          // tenga que recordar el catalogo de memoria.
                          const meta = getModuloMeta(acc.modulo);
                          return (
                            <span
                              key={acc.modulo}
                              title={`${acc.nivel} en ${acc.modulo} - ${meta.name}${meta.description ? ' (' + meta.description + ')' : ''}`}
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                padding: '2px 6px',
                                borderRadius: 3,
                                background: acc.nivel === 'ADMIN' ? C.AZUL2 : C.CELESTE,
                                color: '#fff',
                                fontFamily: 'monospace',
                                letterSpacing: '0.4px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {acc.nivel === 'ADMIN' ? 'A' : 'E'}·{acc.modulo}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  {/* 0596: dropdown 3-dots reemplazado por boton 'Gestionar' que
                      abre el modal unificado openManage(). Mas robusto visualmente
                      (no se rompe en filas finales) y consistente con el patron
                      modal de Estructura. */}
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <button
                      aria-label="Gestionar usuario"
                      onClick={(e) => {
                        e.stopPropagation();
                        openManage(u);
                      }}
                      style={{
                        padding: '4px 10px',
                        background: '#fff',
                        border: `1px solid ${C.AZUL2}`,
                        borderRadius: 4,
                        color: C.AZUL2,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Gestionar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 0588 fix #5: paginacion real + indicador */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          fontSize: 11,
          color: C.GRIS_OSCURO,
        }}
      >
        <div>
          {totalUsers === 0
            ? '0 usuarios'
            : `Mostrando ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalUsers)} de ${totalUsers}`}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Por pagina:
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              style={{ ...INPUT, padding: '4px 6px', fontSize: 11 }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
              style={{
                padding: '4px 10px',
                background: '#fff',
                border: `1px solid ${C.GRIS_MEDIO}`,
                borderRadius: 4,
                cursor: page <= 1 ? 'not-allowed' : 'pointer',
                opacity: page <= 1 ? 0.4 : 1,
                fontFamily: 'inherit',
                fontSize: 11,
              }}
            >
              ‹
            </button>
            <span style={{ minWidth: 60, textAlign: 'center', fontWeight: 600 }}>
              {page} / {totalPages}
            </span>
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
              style={{
                padding: '4px 10px',
                background: '#fff',
                border: `1px solid ${C.GRIS_MEDIO}`,
                borderRadius: 4,
                cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                opacity: page >= totalPages ? 0.4 : 1,
                fontFamily: 'inherit',
                fontSize: 11,
              }}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* ── Bloque colapsable: Roles y permisos ── */}
      <div style={{ marginTop: 20 }}>
        <button
          onClick={() => setShowRbac((v) => !v)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            color: C.AZUL2,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: 0,
          }}
        >
          <span
            style={{
              display: 'inline-block',
              transition: 'transform 0.2s',
              transform: showRbac ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="6,4 20,12 6,20" />
            </svg>
          </span>
          Roles y permisos
        </button>

        {showRbac && (
          <div style={{ marginTop: 12 }}>
            {/* Cards de roles */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
                marginBottom: 12,
              }}
            >
              {ROLES_DATA.map((r) => (
                <div
                  key={r.rol}
                  style={{
                    background: '#fff',
                    borderRadius: 5,
                    border: `1px solid ${C.GRIS_MEDIO}`,
                    borderLeft: `3px solid ${r.color}`,
                    padding: '8px 12px',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 11, color: C.OSCURO }}>{r.rol}</div>
                  <div style={{ fontSize: 10, color: C.GRIS_OSCURO }}>{r.desc}</div>
                </div>
              ))}
            </div>

            {/* Matriz RBAC */}
            <div style={{ borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: C.OSCURO }}>
                    <th style={{ ...TH, textAlign: 'left' }}>Entidad</th>
                    {['SUPER', 'OWN', 'ADM', 'AUD', 'MBR'].map((h) => (
                      <th key={h} style={{ ...TH, textAlign: 'center', width: 60 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {RBAC_DATA.map((row, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: idx % 2 === 1 ? C.SUAVE : undefined,
                      }}
                    >
                      <td
                        style={{
                          padding: '8px 14px',
                          fontWeight: 600,
                          fontSize: 11,
                          color: C.OSCURO,
                        }}
                      >
                        {row.e}
                      </td>
                      {(['SUPER', 'OWN', 'ADM', 'AUD', 'MBR'] as const).map((col) => {
                        const v = row[col];
                        return (
                          <td
                            key={col}
                            style={{
                              padding: '8px 14px',
                              textAlign: 'center',
                              fontFamily: 'monospace',
                              fontSize: 10,
                              fontWeight: 600,
                              color: permColor(v),
                            }}
                          >
                            {v}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 0586: Link a Estructura para gestionar cargos del organigrama */}
            <div
              style={{
                marginTop: 12,
                padding: '10px 14px',
                background: C.ACENTO_SUAVE,
                borderRadius: 6,
                border: `1px solid ${C.SUAVE}`,
                fontSize: 11,
                color: C.OSCURO,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.AZUL2}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
              <span>
                {tu.cargosOrganigramaInfo}{' '}
                <a
                  href="/structure"
                  style={{
                    color: C.AZUL2,
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  {tu.cargosOrganigramaLink}
                </a>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 0572: Modal Invitar usuario */}
      {showInviteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setShowInviteModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 460,
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: C.OSCURO,
                marginBottom: 4,
              }}
            >
              Invitar nuevo usuario
            </h3>
            <p
              style={{
                margin: '0 0 16px',
                fontSize: 11,
                color: C.GRIS_OSCURO,
              }}
            >
              Cada usuario digital queda vinculado a una persona del directorio (Estructura). El
              nombre y apellidos viven en Persona.
            </p>

            {/* 0603 paso 3: selector modo vincular existente | crear nueva */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 16,
                background: C.GRIS,
                padding: 4,
                borderRadius: 6,
              }}
            >
              <button
                type="button"
                onClick={() => setInviteMode('persona')}
                disabled={availablePersonas.length === 0 && !loadingPersonas}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  borderRadius: 4,
                  border: 'none',
                  background: inviteMode === 'persona' ? '#fff' : 'transparent',
                  color: inviteMode === 'persona' ? C.AZUL2 : C.GRIS_OSCURO,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor:
                    availablePersonas.length === 0 && !loadingPersonas ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: inviteMode === 'persona' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                Vincular persona existente
              </button>
              <button
                type="button"
                onClick={() => setInviteMode('nueva')}
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  borderRadius: 4,
                  border: 'none',
                  background: inviteMode === 'nueva' ? '#fff' : 'transparent',
                  color: inviteMode === 'nueva' ? C.AZUL2 : C.GRIS_OSCURO,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: inviteMode === 'nueva' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                Crear persona nueva
              </button>
            </div>

            {/* Modo A: vincular persona existente */}
            {inviteMode === 'persona' && (
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.GRIS_OSCURO,
                    display: 'block',
                    marginBottom: 4,
                  }}
                >
                  Persona del directorio
                </label>
                {loadingPersonas ? (
                  <div
                    style={{
                      padding: '8px 10px',
                      fontSize: 12,
                      color: C.GRIS_OSCURO,
                      fontStyle: 'italic',
                    }}
                  >
                    Cargando personas disponibles...
                  </div>
                ) : (
                  <>
                    <select
                      value={inviteSelectedPersonaId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setInviteSelectedPersonaId(id);
                        const p = availablePersonas.find((x) => x.id === id);
                        if (p) {
                          setInviteEmail(p.email);
                          setInviteNombre(`${p.nombre} ${p.apellidos}`.trim());
                        }
                      }}
                      style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
                    >
                      <option value="">— Selecciona una persona —</option>
                      {availablePersonas.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.codigo} · {p.nombre} {p.apellidos}
                          {p.email ? ` · ${p.email}` : ''}
                        </option>
                      ))}
                    </select>
                    <p
                      style={{
                        fontSize: 11,
                        color: C.GRIS_OSCURO,
                        marginTop: 4,
                        marginBottom: 0,
                      }}
                    >
                      {availablePersonas.length === 0
                        ? 'No hay personas sin usuario digital. Crea una persona nueva o anade personas desde Estructura.'
                        : `${availablePersonas.length} persona(s) disponible(s) sin usuario.`}
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Modo B: crear persona nueva */}
            {inviteMode === 'nueva' && (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.GRIS_OSCURO,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      Nombre
                    </label>
                    <input
                      type="text"
                      placeholder="Alex"
                      value={inviteNombre}
                      onChange={(e) => setInviteNombre(e.target.value)}
                      style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.GRIS_OSCURO,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      Apellidos
                    </label>
                    <input
                      type="text"
                      placeholder="Garcia Sardon"
                      value={inviteApellidos}
                      onChange={(e) => setInviteApellidos(e.target.value)}
                      style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.GRIS_OSCURO,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Tipo de persona
                  </label>
                  <select
                    value={inviteTipoPersona}
                    onChange={(e) =>
                      setInviteTipoPersona(e.target.value as 'EMPLEADO' | 'COLABORADOR' | 'EXTERNO')
                    }
                    style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
                  >
                    <option value="EMPLEADO">EMPLEADO (codigo EMP-NNN)</option>
                    <option value="COLABORADOR">COLABORADOR (codigo COL-NNN)</option>
                    <option value="EXTERNO">EXTERNO (codigo EXT-NNN)</option>
                  </select>
                </div>
              </>
            )}

            {/* Email — comun a ambos modos */}
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.GRIS_OSCURO,
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                Email del usuario digital (login)
              </label>
              <input
                type="email"
                placeholder="usuario@empresa.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
              />
            </div>

            {/* Rol — comun a ambos modos */}
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.GRIS_OSCURO,
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                Rol asignado
              </label>
              <select
                value={inviteRol}
                onChange={(e) => setInviteRol(e.target.value)}
                style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
              >
                <option value="MEMBER">MEMBER (operativo)</option>
                <option value="TENANT_AUDITOR">TENANT_AUDITOR (solo lectura)</option>
                <option value="TENANT_ADMIN">TENANT_ADMIN (admin operativo)</option>
                <option value="TENANT_OWNER">TENANT_OWNER (dueño tenant)</option>
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: `1px solid ${C.GRIS_MEDIO}`,
                  background: '#fff',
                  color: C.GRIS_OSCURO,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleInvite}
                disabled={inviting}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: inviting ? '#9CA3AF' : C.AZUL2,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: inviting ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {inviting ? 'Enviando...' : 'Enviar invitacion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 0596: Modal unificado de gestion de usuario - reemplaza el dropdown 3-dots.
          Click en fila -> abre este modal con todas las acciones. Reglas 0588 (OWNER)
          y 0590 (self) aplicadas: campos disabled si fila propia o si actor no puede. */}
      {manageUser &&
        (() => {
          const isMeManage = !!user?.userId && manageUser.id === user.userId;
          const targetIsOwner = manageUser.rol === 'TENANT_OWNER';
          const cannotTouchOwner = role === 'TENANT_ADMIN' && targetIsOwner;
          return (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
              }}
              onClick={() => setManageUser(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 24,
                  width: 600,
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                }}
              >
                {/* Header con avatar + identidad + badges */}
                <div style={{ display: 'flex', gap: 14, marginBottom: 20, alignItems: 'center' }}>
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      background: C.AZUL2,
                      color: '#fff',
                      fontSize: 18,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {(manageUser.nombre || manageUser.email || '?')
                      .split(' ')
                      .map((p) => p[0])
                      .filter(Boolean)
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 16,
                        fontWeight: 700,
                        color: C.OSCURO,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      {manageUser.nombre}
                      {isMeManage && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.AZUL2,
                            textTransform: 'uppercase',
                          }}
                        >
                          (tú)
                        </span>
                      )}
                    </h3>
                    <p style={{ margin: 0, fontSize: 12, color: C.GRIS_OSCURO }}>
                      {manageUser.email}
                    </p>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <AdminBadge bg={C.ACENTO_SUAVE} color={C.AZUL2}>
                        {manageUser.rol}
                      </AdminBadge>
                      {manageUser.activo ? (
                        <AdminBadge bg={C.VERDE_BG} color={C.VERDE_TXT}>
                          Activo
                        </AdminBadge>
                      ) : (
                        <AdminBadge bg={C.ROJO_BG} color={C.ROJO_TXT}>
                          Inactivo
                        </AdminBadge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Banner reglas si aplica */}
                {(isMeManage || cannotTouchOwner) && (
                  <div
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      fontSize: 12,
                      backgroundColor: '#FEF3C7',
                      color: '#92400E',
                      border: '1px solid #FDE68A',
                      marginBottom: 16,
                    }}
                  >
                    {isMeManage
                      ? 'No puedes cambiar tu propio rol ni desactivarte/eliminarte. Solo puedes editar nombre y persona vinculada.'
                      : 'TENANT_ADMIN no puede modificar a un TENANT_OWNER. Solo TENANT_OWNER o SUPERADMIN_PLATFORM pueden hacerlo.'}
                  </div>
                )}

                {/* Seccion 1: Datos basicos */}
                <h4
                  style={{
                    margin: '0 0 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.GRIS_OSCURO,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {tu.modalDatosBasicos}
                </h4>
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.GRIS_OSCURO,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    value={manageNombre}
                    onChange={(e) => setManageNombre(e.target.value)}
                    disabled={cannotTouchOwner || Boolean(manageUser.personaId)}
                    style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
                  />
                  {/* 0602 Opcion A paso 2: si hay persona vinculada el nombre
                      es read-only y el admin debe ir al modulo Estructura para
                      editarlo (Persona = fuente de verdad). */}
                  {manageUser.personaId && (
                    <div
                      style={{
                        fontSize: 10,
                        color: C.GRIS_OSCURO,
                        marginTop: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>
                        Vinculado a la persona{' '}
                        {manageUser.personaCodigo ? (
                          <strong style={{ fontFamily: 'monospace' }}>
                            {manageUser.personaCodigo}
                          </strong>
                        ) : null}
                        . El nombre se gestiona desde Estructura.
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          // 0608: navegar al modal AdminPersonaPanel via query param
                          // (en lugar de la pantalla obsoleta /structure/admin/personas/:id).
                          // AdminPersonas detecta ?personaId=X y abre el panel auto.
                          navigate(
                            `/structure?tab=administracion&personaId=${manageUser.personaId}`,
                          );
                          setManageUser(null);
                        }}
                        style={{
                          padding: '2px 8px',
                          background: 'transparent',
                          border: `1px solid ${C.AZUL2}`,
                          borderRadius: 4,
                          color: C.AZUL2,
                          fontSize: 10,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        Editar en Estructura →
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.GRIS_OSCURO,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Email
                  </label>
                  <div
                    style={{
                      padding: '8px 10px',
                      border: `1px solid ${C.GRIS_MEDIO}`,
                      borderRadius: 6,
                      fontSize: 13,
                      background: C.GRIS,
                      color: C.GRIS_OSCURO,
                    }}
                  >
                    {manageUser.email}
                  </div>
                  <p style={{ fontSize: 10, color: C.GRIS_OSCURO, marginTop: 4 }}>
                    El email no se puede cambiar desde aqui (afecta al login).
                  </p>
                </div>

                {/* Seccion 2: Permisos y acceso */}
                <h4
                  style={{
                    margin: '16px 0 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.GRIS_OSCURO,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {tu.modalPermisos}
                </h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: C.GRIS_OSCURO,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      Rol RBAC
                    </label>
                    <select
                      value={manageRol}
                      onChange={(e) => setManageRol(e.target.value)}
                      disabled={isMeManage || cannotTouchOwner}
                      style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
                    >
                      {EDITABLE_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: C.GRIS_OSCURO,
                        display: 'block',
                        marginBottom: 4,
                      }}
                    >
                      Estado
                    </label>
                    <select
                      value={manageActivo ? '1' : '0'}
                      onChange={(e) => setManageActivo(e.target.value === '1')}
                      disabled={isMeManage || cannotTouchOwner}
                      style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
                    >
                      <option value="1">Activo</option>
                      <option value="0">Inactivo</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.GRIS_OSCURO,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Persona del organigrama (M1 Structure)
                  </label>
                  <select
                    value={managePersonaId}
                    onChange={(e) => setManagePersonaId(e.target.value)}
                    disabled={cannotTouchOwner}
                    style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
                  >
                    <option value="">— Sin vincular —</option>
                    {personasList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.codigo ? `${p.codigo} · ` : ''}
                        {p.nombre}
                        {p.apellidos ? ' ' + p.apellidos : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: 16, fontSize: 11, color: C.GRIS_OSCURO }}>
                  <strong style={{ color: C.OSCURO }}>Ultimo acceso:</strong> {manageUser.ultimo}
                </div>

                {/* 0599 Gap I: lockout UI - banner + boton desbloquear si aplica */}
                {manageLockout && (manageLockout.locked || manageLockout.attempts > 0) && (
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: 6,
                      fontSize: 12,
                      backgroundColor: manageLockout.locked ? C.ROJO_BG : '#FEF3C7',
                      color: manageLockout.locked ? C.ROJO_TXT : '#92400E',
                      border: `1px solid ${manageLockout.locked ? '#FECACA' : '#FDE68A'}`,
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span>
                      {manageLockout.locked ? (
                        <>
                          <strong>Cuenta bloqueada</strong> · Tras {manageLockout.attempts} intentos
                          fallidos. Desbloqueo automatico en{' '}
                          {Math.ceil(manageLockout.retryAfterSec / 60)} min.
                        </>
                      ) : (
                        <>
                          <strong>{manageLockout.attempts} intento(s) fallido(s)</strong> · Maximo 5
                          antes de bloqueo automatico de 15 min.
                        </>
                      )}
                    </span>
                    {manageLockout.locked && !isMeManage && !cannotTouchOwner && (
                      <button
                        onClick={handleUnlock}
                        style={{
                          padding: '4px 10px',
                          background: '#fff',
                          border: `1px solid ${C.ROJO_TXT}`,
                          borderRadius: 4,
                          color: C.ROJO_TXT,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          flexShrink: 0,
                        }}
                      >
                        Desbloquear
                      </button>
                    )}
                  </div>
                )}

                {/* 0613 ADR-024: Seccion Acceso por modulo. Solo MEMBER (los demas
                    roles tienen niveles implicitos: admin global -> ADMIN, auditor ->
                    VIEWER). Solo visible si NO es self ni OWNER bloqueado. */}
                {manageUser.rol === 'MEMBER' && !isMeManage && !cannotTouchOwner && (
                  <>
                    <h4
                      style={{
                        margin: '16px 0 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.GRIS_OSCURO,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Acceso por modulo
                    </h4>
                    <p
                      style={{
                        fontSize: 11,
                        color: C.GRIS_OSCURO,
                        marginTop: 0,
                        marginBottom: 8,
                      }}
                    >
                      Por defecto un MEMBER es VIEWER (solo ve). Marca <b>Editor</b> para que pueda
                      crear/editar contenido del modulo. Marca <b>Admin</b> para que ademas pueda
                      gestionar la pestana Administracion del modulo (admin marca Editor
                      automaticamente).
                    </p>
                    {modulosActivos.length === 0 ? (
                      <div style={{ fontSize: 12, color: C.GRIS_OSCURO, fontStyle: 'italic' }}>
                        Tenant sin modulos activos.
                      </div>
                    ) : (
                      // 0615d UX: refactor de grid 3-col a tabla con columnas.
                      // Mucho mas escaneable cuando hay 18 modulos. Cada fila es
                      // {Codigo + i + Nivel} | Nombre | Editor | Admin.
                      <div
                        style={{
                          marginBottom: 12,
                          border: `1px solid ${C.GRIS_MEDIO}`,
                          borderRadius: 6,
                          overflow: 'hidden',
                          background: '#fff',
                        }}
                      >
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: 12,
                          }}
                        >
                          <thead>
                            <tr style={{ background: C.GRIS, color: C.GRIS_OSCURO }}>
                              <th
                                style={{
                                  padding: '6px 10px',
                                  textAlign: 'left',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.4px',
                                  width: 130,
                                  borderBottom: `1px solid ${C.GRIS_MEDIO}`,
                                }}
                              >
                                Modulo
                              </th>
                              <th
                                style={{
                                  padding: '6px 10px',
                                  textAlign: 'left',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.4px',
                                  borderBottom: `1px solid ${C.GRIS_MEDIO}`,
                                }}
                              >
                                Nombre
                              </th>
                              <th
                                style={{
                                  padding: '6px 10px',
                                  textAlign: 'center',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.4px',
                                  width: 70,
                                  borderBottom: `1px solid ${C.GRIS_MEDIO}`,
                                }}
                              >
                                Editor
                              </th>
                              <th
                                style={{
                                  padding: '6px 10px',
                                  textAlign: 'center',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.4px',
                                  width: 70,
                                  borderBottom: `1px solid ${C.GRIS_MEDIO}`,
                                }}
                              >
                                Admin
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {modulosActivos.map((modulo, idx) => {
                              // 0615a UX: dropdown VIEWER/EDITOR/ADMIN reemplazado por
                              // 2 checkboxes (Editor + Admin). Reglas:
                              //   sin marcar      -> VIEWER (default)
                              //   solo Editor     -> EDITOR
                              //   solo Admin      -> ADMIN (marcar Admin auto-marca Editor)
                              //   ambos marcados  -> ADMIN
                              const nivelActual: NivelModuloAcceso =
                                manageModuloAccesos[modulo] ?? 'VIEWER';
                              const isEditor = nivelActual === 'EDITOR' || nivelActual === 'ADMIN';
                              const isAdmin = nivelActual === 'ADMIN';
                              const setNivel = (next: NivelModuloAcceso) =>
                                setManageModuloAccesos((prev) => ({ ...prev, [modulo]: next }));
                              const onToggleEditor = (checked: boolean) => {
                                if (checked) {
                                  if (!isEditor) setNivel('EDITOR');
                                } else {
                                  setNivel('VIEWER');
                                }
                              };
                              const onToggleAdmin = (checked: boolean) => {
                                if (checked) {
                                  setNivel('ADMIN');
                                } else {
                                  setNivel('EDITOR');
                                }
                              };
                              const tagBg =
                                nivelActual === 'ADMIN'
                                  ? C.AZUL2
                                  : nivelActual === 'EDITOR'
                                    ? C.CELESTE
                                    : C.GRIS_MEDIO;
                              const tagColor = nivelActual === 'VIEWER' ? C.GRIS_OSCURO : '#fff';
                              // 0615b: meta del modulo (nombre + descripcion).
                              const meta = getModuloMeta(modulo);
                              const tooltip = `${meta.name}${meta.description ? ' — ' + meta.description : ''}`;
                              return (
                                <tr
                                  key={modulo}
                                  style={{
                                    background: idx % 2 === 1 ? C.GRIS : '#fff',
                                    borderBottom:
                                      idx === modulosActivos.length - 1
                                        ? 'none'
                                        : `1px solid ${C.GRIS_MEDIO}`,
                                  }}
                                >
                                  {/* Col 1: codigo + icono i + tag nivel */}
                                  <td style={{ padding: '6px 10px', verticalAlign: 'middle' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span
                                        style={{
                                          fontSize: 11,
                                          fontWeight: 700,
                                          color: C.OSCURO,
                                          fontFamily: 'monospace',
                                          minWidth: 28,
                                        }}
                                      >
                                        {modulo}
                                      </span>
                                      <span
                                        title={tooltip}
                                        aria-label={`Mas info ${meta.name}`}
                                        style={{
                                          fontSize: 9,
                                          fontWeight: 700,
                                          width: 14,
                                          height: 14,
                                          borderRadius: '50%',
                                          border: `1px solid ${C.GRIS_MEDIO}`,
                                          color: C.GRIS_OSCURO,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'help',
                                          flexShrink: 0,
                                          background: '#fff',
                                        }}
                                      >
                                        i
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 9,
                                          fontWeight: 700,
                                          padding: '2px 6px',
                                          borderRadius: 3,
                                          background: tagBg,
                                          color: tagColor,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.4px',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {nivelActual}
                                      </span>
                                    </div>
                                  </td>
                                  {/* Col 2: nombre legible */}
                                  <td
                                    title={tooltip}
                                    style={{
                                      padding: '6px 10px',
                                      verticalAlign: 'middle',
                                      fontSize: 12,
                                      color: C.GRIS_OSCURO,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      maxWidth: 220,
                                    }}
                                  >
                                    {meta.name}
                                  </td>
                                  {/* Col 3: checkbox Editor */}
                                  <td
                                    style={{
                                      padding: '6px 10px',
                                      textAlign: 'center',
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isEditor}
                                      onChange={(e) => onToggleEditor(e.target.checked)}
                                      aria-label={`Editor en ${modulo} ${meta.name}`}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </td>
                                  {/* Col 4: checkbox Admin */}
                                  <td
                                    style={{
                                      padding: '6px 10px',
                                      textAlign: 'center',
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isAdmin}
                                      onChange={(e) => onToggleAdmin(e.target.checked)}
                                      aria-label={`Admin en ${modulo} ${meta.name}`}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {modulosActivos.length > 0 && (
                      <div
                        style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}
                      >
                        <button
                          onClick={handleSaveAccesos}
                          disabled={savingModuloAccesos}
                          style={{
                            padding: '6px 12px',
                            background: savingModuloAccesos ? C.GRIS_MEDIO : C.AZUL2,
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: savingModuloAccesos ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          {savingModuloAccesos ? 'Guardando...' : 'Guardar accesos'}
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Seccion 3: Acciones (solo si NO fila propia y NO bloqueado por OWNER) */}
                {!isMeManage && !cannotTouchOwner && (
                  <>
                    <h4
                      style={{
                        margin: '16px 0 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.GRIS_OSCURO,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Acciones
                    </h4>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          const u = manageUser;
                          setManageUser(null);
                          handleResetPassword(u);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#fff',
                          border: `1px solid ${C.AZUL2}`,
                          borderRadius: 4,
                          color: C.AZUL2,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        Resetear contrasena
                      </button>
                      <button
                        onClick={() => {
                          const u = manageUser;
                          setManageUser(null);
                          handleDelete(u);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#fff',
                          border: `1px solid ${C.ROJO_TXT}`,
                          borderRadius: 4,
                          color: C.ROJO_TXT,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        Eliminar usuario
                      </button>
                    </div>
                  </>
                )}

                {/* Footer */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                    borderTop: `1px solid ${C.GRIS_MEDIO}`,
                    paddingTop: 12,
                  }}
                >
                  <button
                    onClick={() => setManageUser(null)}
                    disabled={savingManage}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: `1px solid ${C.GRIS_MEDIO}`,
                      background: '#fff',
                      color: C.GRIS_OSCURO,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: savingManage ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={handleSaveManage}
                    disabled={savingManage || cannotTouchOwner}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: 'none',
                      background: savingManage || cannotTouchOwner ? '#9CA3AF' : C.AZUL2,
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: savingManage || cannotTouchOwner ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {savingManage ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* 0577: Modal Editar rol */}
      {editRoleUser && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setEditRoleUser(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 420,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: C.OSCURO,
                marginBottom: 16,
              }}
            >
              {tu.cambiarRolTitulo}
            </h3>
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.GRIS_OSCURO,
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                {tu.cambiarRolUsuarioLabel}
              </label>
              <div
                style={{
                  padding: '8px 10px',
                  border: `1px solid ${C.GRIS_MEDIO}`,
                  borderRadius: 6,
                  fontSize: 13,
                  background: C.GRIS,
                  color: C.OSCURO,
                }}
              >
                {editRoleUser.nombre} &lt;{editRoleUser.email}&gt;
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.GRIS_OSCURO,
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                {tu.cambiarRolActual}
              </label>
              <div style={{ fontSize: 13, color: C.GRIS_OSCURO }}>{editRoleUser.rol}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.GRIS_OSCURO,
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                {tu.cambiarRolNuevo}
              </label>
              <select
                value={editingRole}
                onChange={(e) => setEditingRole(e.target.value)}
                style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
              >
                {EDITABLE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: C.GRIS_OSCURO, marginTop: 4, marginBottom: 0 }}>
                {tu.cambiarRolHelp}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setEditRoleUser(null)}
                disabled={savingRole}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: `1px solid ${C.GRIS_MEDIO}`,
                  background: '#fff',
                  color: C.GRIS_OSCURO,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: savingRole ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveRole}
                disabled={savingRole || editingRole === editRoleUser.rol}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: savingRole || editingRole === editRoleUser.rol ? '#9CA3AF' : C.AZUL2,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor:
                    savingRole || editingRole === editRoleUser.rol ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {savingRole ? tu.guardando : tu.guardarCambios}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 0580: Modal Copia password temporal (cuando M17 Notify stub no tiene Resend) */}
      {invitePasswordTemp && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setInvitePasswordTemp(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 480,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: C.OSCURO,
                marginBottom: 12,
              }}
            >
              {tu.copyPasswordTitulo}
            </h3>
            <p style={{ fontSize: 12, color: C.GRIS_OSCURO, marginBottom: 12 }}>
              {tu.copyPasswordHelp.replace('{email}', invitePasswordTemp.email)}
            </p>
            <div
              style={{
                padding: '10px 14px',
                background: C.GRIS,
                border: `1px solid ${C.GRIS_MEDIO}`,
                borderRadius: 6,
                fontFamily: 'monospace',
                fontSize: 14,
                color: C.OSCURO,
                wordBreak: 'break-all',
                marginBottom: 12,
              }}
            >
              {invitePasswordTemp.password}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => {
                  void navigator.clipboard.writeText(invitePasswordTemp.password);
                  showToast(tu.passwordCopiada);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: `1px solid ${C.AZUL2}`,
                  background: '#fff',
                  color: C.AZUL2,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {tu.copiar}
              </button>
              <button
                onClick={() => setInvitePasswordTemp(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: C.AZUL2,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {tu.cerrar}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 0593 Gap B: Modal Asignar persona del organigrama */}
      {assignPersonaUser && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setAssignPersonaUser(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 480,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: C.OSCURO,
                marginBottom: 12,
              }}
            >
              Vincular persona del organigrama
            </h3>
            <p style={{ fontSize: 12, color: C.GRIS_OSCURO, marginBottom: 12, lineHeight: 1.5 }}>
              Vincula a {assignPersonaUser.nombre} ({assignPersonaUser.email}) con una persona del
              organigrama corporativo. La persona se gestiona en el modulo Estructura.
            </p>
            <div style={{ marginBottom: 12 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.GRIS_OSCURO,
                  display: 'block',
                  marginBottom: 4,
                }}
              >
                Persona
              </label>
              <select
                value={selectedPersonaId}
                onChange={(e) => setSelectedPersonaId(e.target.value)}
                style={{ ...INPUT, width: '100%', padding: '8px 10px', fontSize: 13 }}
              >
                <option value="">— Sin vincular —</option>
                {personasList.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.codigo ? `${p.codigo} · ` : ''}
                    {p.nombre}
                    {p.apellidos ? ' ' + p.apellidos : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: C.GRIS_OSCURO, marginTop: 4, marginBottom: 0 }}>
                {personasList.length} personas en el catalogo del tenant.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setAssignPersonaUser(null)}
                disabled={savingPersona}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: `1px solid ${C.GRIS_MEDIO}`,
                  background: '#fff',
                  color: C.GRIS_OSCURO,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: savingPersona ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePersona}
                disabled={savingPersona}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: savingPersona ? '#9CA3AF' : C.AZUL2,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: savingPersona ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {savingPersona ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 0588 fix #4: ConfirmModal custom (sustituye confirm() nativo) */}
      {confirmModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 110,
          }}
          onClick={() => setConfirmModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 420,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: confirmModal.danger ? C.ROJO_TXT : C.OSCURO,
                marginBottom: 12,
              }}
            >
              {confirmModal.titulo}
            </h3>
            <p style={{ fontSize: 13, color: C.GRIS_OSCURO, marginBottom: 16, lineHeight: 1.5 }}>
              {confirmModal.mensaje}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setConfirmModal(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: `1px solid ${C.GRIS_MEDIO}`,
                  background: '#fff',
                  color: C.GRIS_OSCURO,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {tu.cancelar}
              </button>
              <button
                onClick={async () => {
                  const cb = confirmModal.onConfirm;
                  setConfirmModal(null);
                  await cb();
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: confirmModal.danger ? C.ROJO_TXT : C.AZUL2,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 50,
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 500,
            color: '#fff',
            backgroundColor: toast.type === 'warn' ? '#F59E0B' : '#059669',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
